import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
} from '../../../components/ui'
import { QueueListIcon } from '@heroicons/react/20/solid'
import { uiClasses } from '../../shared/uiClasses'
import type { DashboardMetrics, Provider, Report, Task } from '../types'

interface HomeTabProps {
  firstName: string
  metrics: DashboardMetrics
  providers: Provider[]
  reports: Report[]
  tasks: Task[]
}

const TEXT_PRIMARY = uiClasses.textPrimary
const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle

export default function HomeTab({
  firstName,
  metrics,
  providers,
  reports,
  tasks,
}: HomeTabProps) {
  const pendingTasks = tasks.filter((task) => !task.done)
  const nextTasks = pendingTasks.slice(0, 3)
  const disconnectedProviders = providers.filter((provider) => !provider.connected)
  const latestReport = reports[0] ?? null
  const snapshotStats = [
    { label: 'Connected providers', value: metrics.connectedProviders.toString() },
    { label: 'Parsed records', value: metrics.parsedRecords.toString() },
    { label: 'Reports', value: reports.length.toString() },
    { label: 'Plan progress', value: `${metrics.taskProgress}%` },
  ]
  const latestReportLabel = latestReport ? `${latestReport.title} · ${latestReport.createdAt}` : 'No reports generated yet.'

  return (
    <div className="dashboard-home-layout dashboard-home-standard">
      <Card>
        <CardHeader className="flex flex-col gap-4 space-y-0 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1.5">
            <p className={`text-sm font-medium ${TEXT_SUBTLE}`}>Workspace snapshot</p>
            <CardTitle className={`text-3xl tracking-tight ${TEXT_PRIMARY}`}>Your health at a glance</CardTitle>
            <CardDescription className={`max-w-2xl text-base/7 ${TEXT_SECONDARY}`}>
              Everything important for {firstName} is centralized here before you jump into chat, records, or reports.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="text-sm font-medium">
              {pendingTasks.length} open actions
            </Badge>
            <Badge variant="secondary" className="text-sm font-medium">
              {metrics.pendingRecords} records pending parse
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {snapshotStats.map((stat, index) => (
            <article
              key={stat.label}
              className={`dashboard-home-standard-stat dashboard-home-standard-stat-${index + 1} rounded-lg border border-zinc-200 bg-zinc-50/80 p-4`}
            >
              <p className={`text-xs font-medium uppercase tracking-[0.08em] ${TEXT_SUBTLE}`}>{stat.label}</p>
              <p className={`mt-2 text-4xl font-semibold tracking-tight ${TEXT_PRIMARY}`}>{stat.value}</p>
            </article>
          ))}
        </CardContent>
      </Card>

      <div className="dashboard-home-panels">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className={`text-lg ${TEXT_PRIMARY}`}>Priority queue</CardTitle>
            <CardDescription className={TEXT_SECONDARY}>
              The next actions that need attention across your workspace.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {nextTasks.length === 0 ? (
              <EmptyState
                className="dashboard-home-empty-state !py-4"
                icon={QueueListIcon}
                title="No pending actions"
                description="You are caught up."
                titleClassName={TEXT_PRIMARY}
                descriptionClassName={TEXT_SUBTLE}
              />
            ) : (
              <ul className="space-y-3">
                {nextTasks.map((task) => (
                  <li
                    key={task.id}
                    className="dashboard-home-standard-row flex items-start justify-between gap-4 rounded-lg border border-zinc-200 bg-zinc-50/80 px-4 py-3 transition-all hover:border-zinc-300 hover:bg-white hover:shadow-sm"
                  >
                    <span className={`text-sm/6 font-medium ${TEXT_PRIMARY}`}>{task.title}</span>
                    <span className={`text-xs/5 ${TEXT_SUBTLE}`}>{task.due}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className={`text-lg ${TEXT_PRIMARY}`}>Activity snapshot</CardTitle>
            <CardDescription className={TEXT_SECONDARY}>
              Recent status across provider connectivity and reports.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3">
              <div className="dashboard-home-standard-note rounded-lg border border-zinc-200 bg-zinc-50/80 px-4 py-3 transition-all hover:border-zinc-300 hover:bg-white hover:shadow-sm">
                <dt className={`text-xs font-medium uppercase tracking-[0.08em] ${TEXT_SUBTLE}`}>Provider network</dt>
                <dd className={`mt-2 text-base font-medium ${TEXT_PRIMARY}`}>
                  {disconnectedProviders.length === 0
                    ? 'All providers are connected.'
                    : `${disconnectedProviders.length} provider connection(s) need attention.`}
                </dd>
              </div>
              <div className="dashboard-home-standard-note rounded-lg border border-zinc-200 bg-zinc-50/80 px-4 py-3 transition-all hover:border-zinc-300 hover:bg-white hover:shadow-sm">
                <dt className={`text-xs font-medium uppercase tracking-[0.08em] ${TEXT_SUBTLE}`}>Latest report</dt>
                <dd className={`mt-2 text-base font-medium ${TEXT_PRIMARY}`}>{latestReportLabel}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className={`text-lg ${TEXT_PRIMARY}`}>Current snapshot</CardTitle>
          <CardDescription className={`text-base/7 ${TEXT_SECONDARY}`}>
            Move to the Chat page when you want deeper analysis, trend explanations, or a focused next-step plan.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  )
}
